import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState } from 'react';
import Alert from '@/components/alert/alert-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import createCompanyProductApi from '@/api/post/company/company-product/create-company-product';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import uploadToTempS3File from '@/utils/upload-to-s3-file';
import { removeUndefinedProperties } from '@/utils/utils';
import getCompanyProductApi from '@/api/get/company/company-product/get-company-product';
import getCompanyTemplateApi from '@/api/get/company/company-template/get-company-template';
import ProductForm from './product-form';
import { ProductContextProvider } from './context-provider';
import createFlatOptions, {
  recursiveDuplicateProductOptions,
  convertTemplateToProduct,
} from './utils';

/**
 * Add product component.
 * @returns {JSX.Element} The rendered Product Add page component.
 */
const ProductNew = () => {
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [options, setOptions] = useState([]);
  const [initialProductData, setInitialProductData] = useState({});
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const contextValue = {
    prices,
    options,
    setOptions,
    setPrices,
    templateData: initialProductData,
    uniqueContextId: Symbol('product-options-context'),
  };

  const [createProductApi, { loading: createProductLoading }] = useMutation(
    createCompanyProductApi,
  );

  return (
    <ProductContextProvider value={contextValue}>
      <FormWrapper
        defaultValues={async () => {
          if (locationState) {
            if (locationState.templateId) {
              const templateData = await getCompanyTemplateApi(
                id,
                locationState.templateId,
              );

              if (templateData.data) {
                const productDetails = convertTemplateToProduct(
                  templateData,
                  locationState.templateId,
                );

                setInitialProductData(productDetails);

                return productDetails;
              }

              return {
                prices: [{ id: '', description: '' }],
                product: { overrides: {} },
              };
            }
            const product = await getCompanyProductApi(
              id,
              locationState.isProductDuplicate,
            );
            delete product.data.product.id;
            delete product.data.product.name;
            product.data.productOptions = recursiveDuplicateProductOptions(
              product.data.productOptions,
            );

            product.data.categories =
              product.data?.categories?.map((c) => c.category_id) ?? [];

            return product.data;
          }
          return { prices: [{ id: '', description: '' }] };
        }}
        onSubmit={async (values) => {
          try {
            // create product
            setLoading(true);
            const {
              product,
              categories,
              productLinks,
              productOptions,
              productGallery,
              productDocuments,
            } = values;

            delete product.id;
            delete product.created_at;
            delete product.updated_at;

            if (product?.thumbnail?.file) {
              const { file, fileName, fileType } = values.product.thumbnail;
              await uploadToTempS3Base64(
                file,
                `image-upload/${fileName}`,
                fileType,
              ).then((data) => {
                product.thumbnail = `${data.id}`;
              });
            }

            const newGallery = productGallery?.map(async (gallery, index) => {
              if (
                (locationState?.isProductDuplicate ||
                  (locationState?.templateId && gallery.template_gallery_id)) &&
                gallery.id
              ) {
                return {
                  id: 'duplicate',
                  image: gallery.image,
                  order: index,
                };
              }
              const { file } = gallery;
              if (file) {
                return uploadToTempS3File(
                  file,
                  `image-upload/${file.name}-img-${index}`,
                ).then((data) => {
                  const fileKey = data?.id;
                  const updatedGallery = {
                    image: fileKey,
                    order: index,
                  };
                  return updatedGallery;
                });
              }
            });
            const addGallery = await Promise.all(newGallery);

            const newDocuments = productDocuments?.map(async (doc) => {
              if (
                locationState?.isProductDuplicate ||
                (locationState?.templateId && doc.template_document_id)
              ) {
                return {
                  id: 'duplicate',
                  document: doc.document,
                  name: doc.name,
                };
              }
              const { file, fileName } = doc.file;
              if (file) {
                return uploadToTempS3File(
                  file,
                  `document-upload/${fileName}`,
                ).then((data) => {
                  const newDoc = {
                    document: data.id,
                    name: doc.name,
                  };

                  return newDoc;
                });
              }
            });
            const addDocuments = await Promise.all(newDocuments);

            const newOptions =
              productOptions?.length > 0
                ? createFlatOptions(productOptions, [])
                : undefined;

            const addOptions =
              newOptions?.add?.map((option) => {
                const newOption = { ...option };

                delete newOption.option_id;
                delete newOption.product_id;
                delete newOption.isVariant;
                delete newOption.template_id;

                const isParent =
                  newOptions?.add?.filter((o) => o.parent_id === newOption.id)
                    ?.length > 0;

                if (!isParent) {
                  delete newOption.id;
                }

                return {
                  ...newOption,
                };
              }) ?? undefined;

            let addCategories = categories;

            if (
              locationState?.isProductDuplicate ||
              locationState?.templateId
            ) {
              const tempCategories = categories.map((a) => {
                if (a.category_id) {
                  return a.category_id;
                }
                return a;
              });
              // Ensure that there are no duplicates being sent through to the server.
              addCategories = [...new Set([...tempCategories])];
            }

            let newProductLinks = productLinks;

            if (
              locationState?.templateId ||
              locationState?.isProductDuplicate
            ) {
              newProductLinks = productLinks?.map((link) => {
                if (link?.id) {
                  return { link: link?.link };
                }
                return link;
              });
            }

            const payload = {
              product,
              categories: { add: addCategories },
              productGallery: { add: addGallery },
              productDocuments: { add: addDocuments },
              productLinks: { add: newProductLinks },
              productOptions: { add: addOptions },
            };

            createProductApi(id, removeUndefinedProperties(payload))
              .then(() => {
                navigate(-1);
                Alert({
                  icon: 'success',
                  text: 'Product created successfully',
                });
                setLoading(false);
              })
              .catch(() => {
                Alert({
                  icon: 'error',
                  text: 'Product failed to create',
                });
                setLoading(false);
              });
          } catch (e) {
            setLoading(false);
          }
        }}
      >
        <ProductForm type="edit" isLoading={createProductLoading || loading} />
      </FormWrapper>
    </ProductContextProvider>
  );
};

export default ProductNew;
